import React, { useEffect, useState, useCallback } from "react";
import {
  Space,
  Table,
  Tag,
  Typography,
  Input,
  Select,
  Button,
  Dropdown,
  Checkbox,
  message,
  Tooltip,
} from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  listContacts,
  listTags,
  exportContacts,
} from "../../../redux/actions/contactsAction";
import { saveContactsTableColumns } from "../../../redux/actions/organizationAction";

import FormContact from "../../../components/forms/formContact";
import DeleteRecord from "../../../components/modals/deleteRecord";
import ImportContacts from "../../../components/modals/importContacts";
import AttachContactFiles from "../../../components/modals/attachContactFiles";
import BulkTagEditor from "../../../components/modals/bulkTagEditor";
import { canWriteResources } from "../../../utils/rbac";
import useTableScrollY from "../../../hooks/useTableScrollY";

const { Title, Text } = Typography;

const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };
const SHOW_CSV_JSON_EXPORT = false;

const TOGGLEABLE_COLUMNS = [
  { key: "emailAddress", title: "Email Address" },
  { key: "phoneNumber", title: "Phone Number" },
  { key: "companyName", title: "Company" },
  { key: "designation", title: "Designation" },
  { key: "location", title: "Location" },
  { key: "tags", title: "Tags" },
  { key: "leadTier", title: "Lead tier" },
  { key: "createdAt", title: "Created At" },
  { key: "updatedAt", title: "Last Modified" },
];
const TOGGLEABLE_COLUMN_TITLES = Object.fromEntries(
  TOGGLEABLE_COLUMNS.map((col) => [col.key, col.title]),
);
const DEFAULT_VISIBLE_COLUMNS = TOGGLEABLE_COLUMNS.map((col) => col.key);

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateDDMMMYYYY(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mmm = MONTH_ABBREVIATIONS[date.getMonth()];
  const yyyy = date.getFullYear();
  return `${dd}/${mmm}/${yyyy}`;
}

const TAG_COLOR_KEYWORDS = [
  { color: "green", keywords: ["approve", "confirm", "invite"] },
  { color: "blue", keywords: ["new", "register"] },
  { color: "red", keywords: ["reject", "delete", "unsubscribe", "decline"] },
];

function getTagColor(tag) {
  const normalized = String(tag || "").toLowerCase();
  const match = TAG_COLOR_KEYWORDS.find(({ keywords }) =>
    keywords.some((keyword) => normalized.includes(keyword)),
  );
  return match?.color ?? "default";
}

// PRD item 4 (FR-4.5/FR-4.7), task 4.6. Mirrors mailinflow-apis's own
// hot/warm/cold vocabulary (lib/forms/leadScore.js) -- copied onto the
// contact at promotion time, so this is just display, not a live lookup.
const LEAD_TIER_COLORS = { hot: "red", warm: "orange", cold: "blue" };

export default function Contacts() {
  const canWrite = canWriteResources();
  const dispatch = useDispatch();
  const contacts = useSelector((state) => state.contacts.contacts);
  const contactCount = useSelector((state) => state.contacts.contactCount);
  const availableTags = useSelector((state) => state.contacts.tags);
  const activeOrganizationId = useSelector(
    (state) => state.organization.activeOrganizationId,
  );
  const organizations = useSelector((state) => state.organization.organizations);
  const activeOrg = organizations.find(
    (org) => String(org.id) === String(activeOrganizationId),
  );
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    activeOrg?.contactsTableColumns ?? DEFAULT_VISIBLE_COLUMNS,
  );
  const [columnsSaving, setColumnsSaving] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      ...DEFAULT_PAGINATION,
      total: 0,
    },
  });
  const [tableAreaRef, tableScrollY] = useTableScrollY([
    loading,
    contacts.length,
  ]);

  useEffect(() => {
    setVisibleColumns(activeOrg?.contactsTableColumns ?? DEFAULT_VISIBLE_COLUMNS);
  }, [activeOrganizationId, activeOrg?.contactsTableColumns]);

  const persistColumns = (next) => {
    const previous = visibleColumns;
    setVisibleColumns(next);
    setColumnsSaving(true);
    dispatch(saveContactsTableColumns(activeOrganizationId, next))
      .catch(() => {
        setVisibleColumns(previous);
        message.error("Failed to update columns");
      })
      .finally(() => setColumnsSaving(false));
  };

  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key)
      ? visibleColumns.filter((columnKey) => columnKey !== key)
      : [...visibleColumns, key];
    persistColumns(next);
  };

  const moveColumn = (key, direction) => {
    const index = visibleColumns.indexOf(key);
    const newIndex = index + direction;
    if (index === -1 || newIndex < 0 || newIndex >= visibleColumns.length) {
      return;
    }
    const next = [...visibleColumns];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    persistColumns(next);
  };

  const nameColumn = {
    title: "Name",
    dataIndex: "firstName",
    key: "firstName",
    render: (_, record) => (
      <>
        {record.firstName} {record.lastName}
      </>
    ),
  };
  const emailColumn = {
    title: "Email Address",
    dataIndex: "emailAddress",
    key: "emailAddress",
  };
  const phoneColumn = {
    title: "Phone Number",
    dataIndex: "phoneNumber",
    key: "phoneNumber",
  };
  const companyColumn = {
    title: "Company",
    dataIndex: "companyName",
    key: "companyName",
  };
  const designationColumn = {
    title: "Designation",
    dataIndex: "designation",
    key: "designation",
  };
  const locationColumn = {
    title: "Location",
    dataIndex: "location",
    key: "location",
  };
  const createdAtColumn = {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => formatDateDDMMMYYYY(value),
  };
  const updatedAtColumn = {
    title: "Last Modified",
    dataIndex: "updatedAt",
    key: "updatedAt",
    render: (value, record) =>
      formatDateDDMMMYYYY(value || record.createdAt),
  };
  const tagsColumn = {
    title: "Tags",
    key: "tags",
    dataIndex: "tags",
    render: (_, { tags }) => {
      const allTags = tags || [];
      const latestTags = allTags.slice(-2).reverse();
      const olderTags = allTags.slice(0, -2);
      return (
        <>
          {latestTags.map((tag) => (
            <Tag color={getTagColor(tag)} key={tag}>
              {tag.toUpperCase()}
            </Tag>
          ))}
          {olderTags.length > 0 && (
            <Tooltip
              title={
                <Space size={4} wrap>
                  {olderTags.map((tag) => (
                    <Tag color={getTagColor(tag)} key={tag}>
                      {tag.toUpperCase()}
                    </Tag>
                  ))}
                </Space>
              }
            >
              <Tag>+{olderTags.length} more</Tag>
            </Tooltip>
          )}
        </>
      );
    },
  };
  const leadTierColumn = {
    title: "Lead tier",
    key: "leadTier",
    dataIndex: "leadTier",
    render: (tier, record) =>
      tier ? (
        <Tooltip title={`Score ${record.leadScore ?? "—"}/100`}>
          <Tag color={LEAD_TIER_COLORS[tier] || "default"}>
            {tier.toUpperCase()}
          </Tag>
        </Tooltip>
      ) : (
        <Text type="secondary">—</Text>
      ),
  };
  const actionColumn = {
    title: "Action",
    key: "action",
    render: (_, record) =>
      canWrite ? (
        <Space>
          <FormContact
            type="edit"
            formData={record}
            fetchRecords={() =>
              fetchContacts(tableParams.pagination, {
                search: searchInput,
                tags: selectedTags,
              })
            }
          />
          <DeleteRecord
            id={record._id}
            collection="contacts"
            fetchRecords={() =>
              fetchContacts(tableParams.pagination, {
                search: searchInput,
                tags: selectedTags,
              })
            }
          />
        </Space>
      ) : (
        <Text type="secondary">View only</Text>
      ),
  };

  const TOGGLEABLE_COLUMN_DEFS = {
    emailAddress: emailColumn,
    phoneNumber: phoneColumn,
    companyName: companyColumn,
    designation: designationColumn,
    location: locationColumn,
    tags: tagsColumn,
    leadTier: leadTierColumn,
    createdAt: createdAtColumn,
    updatedAt: updatedAtColumn,
  };

  const columns = [
    nameColumn,
    ...visibleColumns.map((key) => TOGGLEABLE_COLUMN_DEFS[key]).filter(Boolean),
    actionColumn,
  ];

  const columnsForDisplay = [
    ...visibleColumns.filter((key) => TOGGLEABLE_COLUMN_DEFS[key]),
    ...TOGGLEABLE_COLUMNS.map((col) => col.key).filter(
      (key) => !visibleColumns.includes(key),
    ),
  ];

  const fetchContacts = useCallback(
    (pagination, filters = {}) => {
      if (!pagination) {
        pagination = { ...DEFAULT_PAGINATION };
      }
      const search = filters.search ?? searchInput;
      const tags = filters.tags ?? selectedTags;

      setLoading(true);
      const payload = {
        pageSize: pagination.pageSize,
        pageNum: pagination.current,
      };
      if (search?.trim()) {
        payload.search = search.trim();
      }
      if (tags?.length) {
        payload.tags = tags.join(",");
      }

      return dispatch(listContacts(payload))
        .then(() => {
          setTableParams({
            pagination: { ...pagination },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, searchInput, selectedTags],
  );

  const handleTableChange = (pagination) => {
    fetchContacts(pagination, { search: searchInput, tags: selectedTags });
  };

  const applyFilters = () => {
    setSelectedRowKeys([]);
    fetchContacts(
      { ...tableParams.pagination, current: 1 },
      { search: searchInput, tags: selectedTags },
    );
  };

  const clearFilters = () => {
    setSearchInput("");
    setSelectedTags([]);
    setSelectedRowKeys([]);
    fetchContacts(
      { ...tableParams.pagination, current: 1 },
      { search: "", tags: [] },
    );
  };

  const hasActiveFilters =
    Boolean(searchInput.trim()) || selectedTags.length > 0;

  const downloadExport = async (format) => {
    setExporting(true);
    const ids = selectedRowKeys.length ? selectedRowKeys : undefined;
    try {
      const res = await exportContacts({
        format,
        ids,
        search: ids ? undefined : searchInput,
        tags: ids ? undefined : selectedTags,
        columns: visibleColumns,
      });
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === "csv" || format === "xlsx") {
        const mimeType =
          format === "csv"
            ? "text/csv"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: mimeType }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `contacts-${stamp}.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        const payload = res.data?.result ?? res.data;
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `contacts-${stamp}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
      message.success("Export downloaded");
    } catch {
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportLabel = selectedRowKeys.length
    ? `${selectedRowKeys.length} selected`
    : hasActiveFilters
      ? "filtered"
      : "all";

  useEffect(() => {
    dispatch(listTags());
    fetchContacts(DEFAULT_PAGINATION, { search: "", tags: [] });
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const tagOptions = (availableTags || [])
    .filter(Boolean)
    .map((tag) => ({ value: tag, label: tag }));

  return (
    <div className="Contacts">
      <Space
        wrap
        style={{
          display: "flex",
          margin: "0 0.5rem 0.25rem 0.5rem",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Contacts
          </Title>
          <Text type="secondary">Manage your contacts</Text>
        </div>
        <Space size="small">
          {canWrite ? (
            <>
              <ImportContacts
                fetchRecords={() =>
                  fetchContacts(tableParams.pagination, {
                    search: searchInput,
                    tags: selectedTags,
                  })
                }
              />
              <AttachContactFiles
                fetchRecords={() =>
                  fetchContacts(tableParams.pagination, {
                    search: searchInput,
                    tags: selectedTags,
                  })
                }
              />
              <FormContact
                type="create"
                fetchRecords={() =>
                  fetchContacts(tableParams.pagination, {
                    search: searchInput,
                    tags: selectedTags,
                  })
                }
              />
            </>
          ) : null}
        </Space>
      </Space>

      <Space wrap style={{ margin: "0 0.5rem 1rem 0.5rem", width: "100%" }}>
        <Input.Search
          placeholder="Search name, email, or phone"
          allowClear
          className="app-filter-control"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={applyFilters}
        />
        <Select
          mode="multiple"
          placeholder="Filter by tags"
          allowClear
          style={{ minWidth: 220 }}
          value={selectedTags}
          options={tagOptions}
          onChange={setSelectedTags}
          maxTagCount="responsive"
        />
        <Button type="primary" onClick={applyFilters}>
          Apply
        </Button>
        {hasActiveFilters ? (
          <Button onClick={clearFilters}>Clear</Button>
        ) : null}
        {SHOW_CSV_JSON_EXPORT ? (
          <>
            <Button loading={exporting} onClick={() => downloadExport("csv")}>
              Export CSV ({exportLabel})
            </Button>
            <Button loading={exporting} onClick={() => downloadExport("json")}>
              Export JSON ({exportLabel})
            </Button>
          </>
        ) : null}
        <Button loading={exporting} onClick={() => downloadExport("xlsx")}>
          Export Excel ({exportLabel})
        </Button>
        <Dropdown
          trigger={["click"]}
          dropdownRender={() => (
            <div
              style={{
                background: "var(--ant-color-bg-elevated, #fff)",
                padding: 12,
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Space direction="vertical">
                {columnsForDisplay.map((key) => {
                  const isVisible = visibleColumns.includes(key);
                  const visibleIndex = visibleColumns.indexOf(key);
                  return (
                    <Space key={key}>
                      <Checkbox
                        checked={isVisible}
                        onChange={() => toggleColumn(key)}
                      >
                        {TOGGLEABLE_COLUMN_TITLES[key]}
                      </Checkbox>
                      {isVisible ? (
                        <>
                          <Button
                            size="small"
                            type="text"
                            icon={<UpOutlined />}
                            disabled={visibleIndex === 0}
                            onClick={() => moveColumn(key, -1)}
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<DownOutlined />}
                            disabled={visibleIndex === visibleColumns.length - 1}
                            onClick={() => moveColumn(key, 1)}
                          />
                        </>
                      ) : null}
                    </Space>
                  );
                })}
              </Space>
            </div>
          )}
        >
          <Button loading={columnsSaving}>Columns</Button>
        </Dropdown>
        {canWrite ? (
          <BulkTagEditor
            selectedIds={selectedRowKeys}
            availableTags={availableTags}
            fetchRecords={() => {
              fetchContacts(tableParams.pagination, {
                search: searchInput,
                tags: selectedTags,
              });
              dispatch(listTags());
            }}
            onDone={() => setSelectedRowKeys([])}
          />
        ) : null}
      </Space>

      <div ref={tableAreaRef} style={{ width: "100%" }}>
        <Table
          loading={loading}
          dataSource={contacts}
          scroll={{
            x: 1000,
            y: tableScrollY,
          }}
          columns={columns}
          rowKey="_id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            ...tableParams.pagination,
            total: contactCount,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (totalCount) => `Total  ${totalCount}  items`,
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
